import React, { useState } from 'react';
import bcrypt from 'bcryptjs';
import './LoginPage.css';

const ADMIN_EMAIL  = process.env.REACT_APP_ADMIN_EMAIL;
const MASTER_HASH  = process.env.REACT_APP_MASTER_HASH; // fixed, never changes
const API          = process.env.REACT_APP_API_URL;

// ─── Session helpers ──────────────────────────────────────────────
const SESSION_KEY = 'eb_admin_session';

export function isLoggedIn() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const { token, exp } = JSON.parse(raw);
    if (Date.now() > exp) { sessionStorage.removeItem(SESSION_KEY); return false; }
    return token === 'authenticated';
  } catch { return false; }
}

export function doLogin(passwordVersion) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({
    token: 'authenticated',
    exp: Date.now() + 8 * 60 * 60 * 1000, // 8 hours
    passwordVersion: passwordVersion || 1,
  }));
}

// Check if password version has changed (called periodically)
export async function checkPasswordVersion() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return true; // Not logged in, no need to check

    const { passwordVersion } = JSON.parse(raw);
    if (!passwordVersion) return true;

    // Fetch current password version from backend
    const response = await fetch(`${API}/api/admin/login-hash`);
    const data = await response.json();
    const currentVersion = data.version;

    // If versions don't match, password was changed elsewhere
    if (currentVersion !== passwordVersion) {
      console.log('Password changed on another device - logging out');
      doLogout();
      return false; // Signal that logout occurred
    }
    return true;
  } catch (err) {
    console.error('Password version check error:', err);
    return true; // Don't logout on network errors
  }
}

export function doLogout() {
  sessionStorage.removeItem(SESSION_KEY);
}

// ─── Component ────────────────────────────────────────────────────
export default function LoginPage({ onLogin }) {
  const [view, setView] = useState('login'); // 'login' | 'forgot'

  // Login form
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [loginErr, setLoginErr]   = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [showPw, setShowPw]       = useState(false);

  // Forgot password form
  const [masterPw, setMasterPw]       = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [forgotErr, setForgotErr]     = useState('');
  const [forgotOk, setForgotOk]       = useState('');
  const [forgotBusy, setForgotBusy]   = useState(false);
  const [showMaster, setShowMaster]   = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Login submit ──────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    if (!email.trim() || !password) {
      setLoginErr('Please enter your email and password.');
      return;
    }
    if (email.trim().toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
      setLoginErr('Invalid email or password.');
      return;
    }
    setLoginBusy(true);
    try {
      // Fetch current hash from backend (may have been changed via forgot-password)
      const res  = await fetch(`${API}/api/admin/login-hash`);
      const data = await res.json();
      if (!data.success || !data.hash) {
        setLoginErr('Could not reach server. Please try again.');
        setLoginBusy(false);
        return;
      }
      const ok = await bcrypt.compare(password, data.hash);
      if (ok) {
        doLogin(data.version);
        onLogin();
      } else {
        setLoginErr('Invalid email or password.');
      }
    } catch {
      setLoginErr('Could not reach server. Please check your connection.');
    } finally {
      setLoginBusy(false);
    }
  };

  // ── Forgot password submit ────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotErr('');
    setForgotOk('');
    if (!masterPw || !newPw || !confirmPw) {
      setForgotErr('All fields are required.');
      return;
    }
    if (newPw.length < 8) {
      setForgotErr('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setForgotErr('New passwords do not match.');
      return;
    }
    setForgotBusy(true);
    try {
      // Hash the new password
      const salt    = await bcrypt.genSalt(12);
      const newHash = await bcrypt.hash(newPw, salt);

      // Send master password and new hash to backend for verification and saving
      const res  = await fetch(`${API}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterPassword: masterPw,  // Send plaintext master password for backend verification
          newHash                     // Send hashed new password
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setForgotErr(data.message || 'Failed to save new password.');
        setForgotBusy(false);
        return;
      }
      setForgotOk('Password changed successfully! You can now log in with your new password.');
      setMasterPw(''); setNewPw(''); setConfirmPw('');
    } catch {
      setForgotErr('Could not reach server. Please check your connection.');
    } finally {
      setForgotBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-card">

        {/* Brand */}
        <div className="login-brand">
          <div className="login-brand-icon">⚙</div>
          <h1 className="login-brand-title">Elite Borewells</h1>
          <p className="login-brand-sub">Admin Panel</p>
        </div>

        {view === 'login' ? (
          <>
            <h2 className="login-heading">Sign In</h2>
            <form className="login-form" onSubmit={handleLogin} autoComplete="off">

              <div className="lf-group">
                <label className="lf-label">Email</label>
                <input
                  className="lf-input"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  disabled={loginBusy}
                />
              </div>

              <div className="lf-group">
                <label className="lf-label">Password</label>
                <div className="lf-pw-wrap">
                  <input
                    className="lf-input"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={loginBusy}
                  />
                  <button
                    type="button"
                    className="lf-eye"
                    onClick={() => setShowPw((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >{showPw ? '🙈' : '👁'}</button>
                </div>
              </div>

              {loginErr && <p className="lf-error">{loginErr}</p>}

              <button className="lf-btn" type="submit" disabled={loginBusy}>
                {loginBusy ? 'Signing in…' : 'Sign In'}
              </button>

              <button
                type="button"
                className="lf-forgot-link"
                onClick={() => { setView('forgot'); setLoginErr(''); }}
              >
                Forgot Password?
              </button>

            </form>
          </>
        ) : (
          <>
            <h2 className="login-heading">Reset Password</h2>
            <p className="login-subtext">Enter the master password to set a new login password.</p>
            <form className="login-form" onSubmit={handleForgot} autoComplete="off">

              <div className="lf-group">
                <label className="lf-label">Master Password</label>
                <div className="lf-pw-wrap">
                  <input
                    className="lf-input"
                    type={showMaster ? 'text' : 'password'}
                    placeholder="Enter master password"
                    value={masterPw}
                    onChange={(e) => setMasterPw(e.target.value)}
                    disabled={forgotBusy}
                    autoComplete="off"
                  />
                  <button type="button" className="lf-eye" onClick={() => setShowMaster((v) => !v)} tabIndex={-1}>
                    {showMaster ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <div className="lf-group">
                <label className="lf-label">New Password</label>
                <div className="lf-pw-wrap">
                  <input
                    className="lf-input"
                    type={showNew ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    disabled={forgotBusy}
                    autoComplete="new-password"
                  />
                  <button type="button" className="lf-eye" onClick={() => setShowNew((v) => !v)} tabIndex={-1}>
                    {showNew ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <div className="lf-group">
                <label className="lf-label">Confirm New Password</label>
                <div className="lf-pw-wrap">
                  <input
                    className="lf-input"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repeat new password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    disabled={forgotBusy}
                    autoComplete="new-password"
                  />
                  <button type="button" className="lf-eye" onClick={() => setShowConfirm((v) => !v)} tabIndex={-1}>
                    {showConfirm ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              {forgotErr && <p className="lf-error">{forgotErr}</p>}
              {forgotOk  && <p className="lf-success">{forgotOk}</p>}

              <button className="lf-btn" type="submit" disabled={forgotBusy}>
                {forgotBusy ? 'Saving…' : 'Reset Password'}
              </button>

              <button
                type="button"
                className="lf-forgot-link"
                onClick={() => { setView('login'); setForgotErr(''); setForgotOk(''); }}
              >
                Back to Sign In
              </button>

            </form>
          </>
        )}
      </div>
    </div>
  );
}
